import {
  Add,
  Delete,
  ExpandLess,
  ExpandMore,
  Receipt,
  SettingsBackupRestore,
} from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  expresGetDocumentFile,
  expressUploadFilesFromRequest,
} from 'lib/api/express/factoring';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Uploader from 'lib/designSystem/Uploader';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import {
  calculateDays,
  getRealDaysToCount,
  isWorkingDay,
} from 'lib/functions/dates';
import {
  formatDate,
  formatMoney,
  formatMoneyBack,
  hasAnyPermissions,
  loggerDev,
  showAlert,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AuthState } from '../../../../../../../../../../../../services/redux/authSlice';

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%', // ensure the table fits its container
    },
    tableCell: {
      padding: '4px !important',
      maxWidth: '200px',
      maxHeight: '10px',
      overflow: 'hidden', // Ensure the content doesn't overflow.
      whiteSpace: 'nowrap', // Ensure the text doesn't wrap to the next line.
    },
    tableContainer: {
      border: '1px solid #e0e0e0',
      borderRadius: '10px',
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
    selectFullWidth: {
      width: '100%',
    },
    selectSmall: {
      width: '200px', // or any other small width
    },
  }),
);

const firstCellStyle: any = {
  maxWidth: '150px',
  overflow: 'hidden', // Ensure the content doesn't overflow.
  textOverflow: 'ellipsis', // If the text overflows, it will end with "..."
  whiteSpace: 'nowrap', // Ensure the text doesn't wrap to the next line.
  padding: '4px 16px',
};

const DocumentsTable = (props: any) => {
  const {
    selectedDocuments,
    setSelectedDocuments,
    canForcePaymentDate,
    minimumDays,
    isExpress,
    clientId,
    operationDate,
  } = props;
  const classes = useStyles();
  const [openUploadDocumentPDF, setOpenUploadDocumentPDF] = useState(false);
  const [file, setFile] = useState<any>(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [documentToUpload, setDocumentToUpload] = useState('');
  const [loading, setLoading] = useState(false);
  const [anchorEls, setAnchorEls] = useState<any>(
    selectedDocuments.map(() => null),
  );
  const [openMenus, setOpenMenus] = useState<any>(
    selectedDocuments.map(() => false),
  );
  const [expandedRows, setExpandedRows] = useState<any>({});
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  // Functions to handle documents changes
  const dateValidators = (date: Date, rowIndex: number) => {
    if (canForcePaymentDate) return true;

    const actualDate = selectedDocuments[rowIndex].paymentDate;
    if (!isWorkingDay(date)) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento debe ser un día hábil',
      );
      handleChangeTempDate(formatDate(actualDate), rowIndex);
      return false;
    }
    const minimumDate = new Date(operationDate);
    minimumDate.setDate(minimumDate.getDate() + minimumDays - 1);
    if (date < minimumDate) {
      showAlert(
        dispatch,
        'warning',
        `La fecha de vencimiento no puede ser menor a ${minimumDays} días a partir de la fecha de solicitud`,
      );
      handleChangeTempDate(formatDate(actualDate), rowIndex);
      return false;
    }
    const days = calculateDays(
      new Date(selectedDocuments[rowIndex].issuedDate),
      new Date(date),
    );
    if (days <= 0) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento no puede ser menor a la fecha de emisión',
      );
      return false;
    }
    return days;
  };

  const handleChangeTempDate = (value: any, rowIndex: number) => {
    const newSelectedDocuments = selectedDocuments.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          tempDueDate: value,
        };
      }
      return doc;
    });
    setSelectedDocuments(newSelectedDocuments);
  };

  const handleChangeDateNormal = (index: number) => {
    const value = selectedDocuments[index].tempDueDate;
    const date = new Date(value);

    const days = dateValidators(date, index);
    if (!days) return;
    const newSelectedDocuments = selectedDocuments.map((doc: any, i: any) => {
      if (i === index) {
        return {
          ...doc,
          dueDate: date?.toISOString(),
          paymentDate: date,
          tempDueDate: formatDate(date),
          daysToCount: getRealDaysToCount(date, operationDate),
        };
      }
      return doc;
    });

    setSelectedDocuments(newSelectedDocuments);
  };

  // Functions to handle sub-documents changes
  const dateValidatorsSubIndex = (
    date: Date,
    rowIndex: number,
    subIndex: number,
  ) => {
    if (canForcePaymentDate) return true;

    const actualDate =
      selectedDocuments[rowIndex].subDocuments[subIndex].paymentDate;
    if (!isWorkingDay(date)) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento debe ser un día hábil',
      );
      handleChangeSubDocumentTempDate(
        formatDate(actualDate),
        rowIndex,
        subIndex,
      );
      return false;
    }
    const minimumDate = new Date(operationDate);
    minimumDate.setDate(minimumDate.getDate() + minimumDays - 1);
    if (date < minimumDate) {
      showAlert(
        dispatch,
        'warning',
        `La fecha de vencimiento no puede ser menor a ${minimumDays} días a partir de la fecha de solicitud`,
      );
      handleChangeSubDocumentTempDate(
        formatDate(actualDate),
        rowIndex,
        subIndex,
      );
      return false;
    }
    const days = calculateDays(
      new Date(selectedDocuments[rowIndex].subDocuments[subIndex].issuedDate),
      new Date(date),
    );
    if (days <= 0) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento no puede ser menor a la fecha de emisión',
      );
      return false;
    }
    return days;
  };

  const handleChangeSubDocumentTempDate = (
    value: any,
    rowIndex: number,
    subIndex: number,
  ) => {
    const newSelectedDocuments = selectedDocuments.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          subDocuments: doc.subDocuments.map((subDoc: any, j: any) =>
            j === subIndex
              ? {
                  ...subDoc,
                  tempDueDate: value,
                }
              : subDoc,
          ),
        };
      }
      return doc;
    });
    setSelectedDocuments(newSelectedDocuments);
  };

  const handleChangeSubDocumentDateNormal = (
    rowIndex: number,
    subIndex: number,
  ) => {
    const value =
      selectedDocuments[rowIndex].subDocuments[subIndex].tempDueDate;
    const date = new Date(value);
    const days = dateValidatorsSubIndex(date, rowIndex, subIndex);
    if (!days) return;
    const newSelectedDocuments = selectedDocuments.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          subDocuments: doc.subDocuments.map((subDoc: any, j: any) => {
            if (j === subIndex) {
              return {
                ...subDoc,
                dueDate: date?.toISOString(),
                paymentDate: date,
                daysToCount: getRealDaysToCount(date, operationDate),
              };
            }
            return subDoc;
          }),
        };
      }
      return doc;
    });
    setSelectedDocuments(newSelectedDocuments);
  };

  const addSubDocument = (rowIndex: number) => {
    const newSelectedDocuments = selectedDocuments.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          daysToCount: null,
          subDocuments: [
            ...(doc.subDocuments ? doc.subDocuments : []),
            {
              id: null,
              amount: 0,
              amountAvailable: 0,
              businessIdentifier:
                selectedDocuments[rowIndex].businessIdentifier,
              cancelDate: null,
              companyId: selectedDocuments[rowIndex].companyId,
              confirmDate: null,
              factoringQuotes: [],
              dueDate: undefined,
              paymentDate: undefined,
              tempDueDate: undefined,
              fatherDocumentId: selectedDocuments[rowIndex].id,
              fatherId: selectedDocuments[rowIndex].id,
              folio: null,
              isOld: false,
              issuer: true,
              nulledAt: null,
              paidAt: null,
              issuedDate: selectedDocuments[rowIndex].issuedDate,
              pendingAccountId: null,
              stakeholder: selectedDocuments[rowIndex].stakeholder,
              stakeholderIdentifier:
                selectedDocuments[rowIndex].stakeholderIdentifier,
              stakeholderName: selectedDocuments[rowIndex].stakeholderName,
              subDocuments: [],
              type: selectedDocuments[rowIndex].type,
              daysToCount: null,
            },
          ],
        };
      }
      return doc;
    });
    setSelectedDocuments(newSelectedDocuments);
    if (!expandedRows[rowIndex]) toggleRowExpansion(rowIndex);
  };

  const handleChangeSubDocument = (
    value: any,
    name: any,
    rowIndex: number,
    subIndex: number,
  ) => {
    const newSelectedDocuments = selectedDocuments.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          subDocuments: doc.subDocuments.map((subDoc: any, j: any) => {
            if (j === subIndex) {
              return {
                ...subDoc,
                [name]: name === 'amount' ? formatMoneyBack(value) : value,
              };
            }
            return subDoc;
          }),
        };
      }
      return doc;
    });
    setSelectedDocuments(newSelectedDocuments);
  };

  const deleteSubDocument = (rowIndex: number, subIndex: number) => {
    const newSelectedDocuments = selectedDocuments.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          subDocuments: doc.subDocuments.filter(
            (subDoc: any, j: any) => j !== subIndex,
          ),
        };
      }
      return doc;
    });
    setSelectedDocuments(newSelectedDocuments);
  };

  // Functions to handle row expansion
  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows({
      ...expandedRows,
      [rowIndex]: !expandedRows[rowIndex],
    });
  };

  const toggleRowExpansionPdf = (
    rowIndex: number,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    const newOpenMenus = openMenus.map((el: any, i: number) =>
      i === rowIndex ? !el : false,
    );
    const newAnchorEls = anchorEls.map((el: any, i: number) =>
      i === rowIndex ? event.currentTarget : null,
    );
    setAnchorEls(newAnchorEls);
    setOpenMenus(newOpenMenus);
    const document = selectedDocuments[rowIndex];
    const documentKey = `${document.businessIdentifier}-${document.folio}`;
    setDocumentToUpload(documentKey);
  };

  useEffect(() => {
    const newSelectedDocuments = selectedDocuments.map((doc: any) => {
      return {
        ...doc,
        paymentDate: doc.dueDate,
        tempDueDate: formatDate(doc.dueDate),
        daysToCount:
          doc.dueDate && doc.dueDate !== ''
            ? getRealDaysToCount(doc.dueDate, operationDate)
            : '',
        isOld: doc.isOld,
        subDocuments:
          doc?.subDocuments?.map((subDoc: any) => ({
            ...subDoc,
            folio: subDoc.folio
              ? subDoc.folio.split('-')[1] || subDoc.folio
              : null,
            daysToCount:
              subDoc.dueDate && subDoc.dueDate !== ''
                ? getRealDaysToCount(subDoc.dueDate, operationDate)
                : '',
            isOld: subDoc.isOld,
            paymentDate: subDoc.dueDate,
            tempDueDate: formatDate(subDoc.dueDate),
          })) || [],
      };
    });
    setSelectedDocuments(newSelectedDocuments);
  }, []);

  const dispatch = useDispatch();

  const handleFileChange = (event: any) => {
    const uploadedFile = event.target.files[0];
    setUploadedFileName(uploadedFile.name);
    setFile(uploadedFile);
  };

  const handleUploadDocumentPDF = () => {
    setOpenUploadDocumentPDF(true);
  };

  const handleDownloadDocumentPDF = async (documentId: string) => {
    const document = selectedDocuments.find((x: any) => x.id === documentId);
    const documentKey = `${document.businessIdentifier}-${document.folio}.pdf`;
    setLoading(true);
    try {
      const response = await expresGetDocumentFile(
        dispatch,
        clientId,
        documentKey,
      );
      if (response) {
        window.open(response);
      } else {
        showAlert(dispatch, 'warning', 'No hay archivo para descargar');
      }
    } catch (error) {
      loggerDev(error);
    }
    setLoading(false);
  };

  const uploadFiles = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      const fileToUpload = new File([file], `${documentToUpload}.pdf`, {
        type: file.type,
      });
      formData.append('files', fileToUpload);
      await expressUploadFilesFromRequest(dispatch, clientId, formData);
      showAlert(dispatch, 'success', 'Se han subido tus archivos con éxito');
      setDocumentToUpload('');
      const documents = selectedDocuments.map((document: any) => {
        if (
          `${document.businessIdentifier}-${document.folio}` ===
          documentToUpload
        ) {
          return {
            ...document,
            fileStatus: true,
          };
        }
        return document;
      });
      setSelectedDocuments(documents);
      setOpenUploadDocumentPDF(false);
    } catch (error) {
      loggerDev(error);
    }
    setLoading(false);
  };

  // Handler para editar el monto del documento principal
  const handleChangeDocumentAmount = (value: any, rowIndex: number) => {
    const newSelectedDocuments = selectedDocuments.map((doc: any, i: any) => {
      if (i === rowIndex) {
        if (doc.subDocuments && doc.subDocuments.length > 0) {
          return doc;
        }
        const newAmount = formatMoneyBack(value);

        if (
          doc.originalAmount !== undefined &&
          newAmount > doc.originalAmount
        ) {
          showAlert(
            dispatch,
            'warning',
            `El monto no puede ser mayor al valor original del documento (${formatMoney(doc.originalAmount)})`,
          );
          return doc;
        }
        const { amountAvailable, ...rest } = doc;
        return {
          ...rest,
          amount: newAmount,
          amountAvailable: newAmount,
        };
      }
      return doc;
    });
    setSelectedDocuments(newSelectedDocuments);
  };

  const anyHasSubDocuments = selectedDocuments.some(
    (document: any) =>
      document.subDocuments && document.subDocuments.length > 0,
  );

  return (
    <TableContainer className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead style={{ background: '#efefef' }}>
          <TableCell style={firstCellStyle}>Sub documentos</TableCell>
          <TableCell style={firstCellStyle}>Folio</TableCell>
          {isExpress && <TableCell style={firstCellStyle}>PDF</TableCell>}
          {selectedDocuments[0]?.code && (
            <TableCell style={firstCellStyle}>Código Doc.</TableCell>
          )}
          <TableCell
            className={classes.tableCell}
            style={{ textAlign: 'right' }}
          >
            Nombre deudor
          </TableCell>
          <TableCell
            className={classes.tableCell}
            style={{ textAlign: 'right' }}
          >
            Monto total
          </TableCell>
          {anyHasSubDocuments && (
            <TableCell
              className={classes.tableCell}
              style={{ textAlign: 'right' }}
            >
              Monto disp.
            </TableCell>
          )}
          <TableCell
            className={classes.tableCell}
            style={{ textAlign: 'right' }}
          >
            Fecha emisión
          </TableCell>
          <TableCell
            className={classes.tableCell}
            style={{ textAlign: 'right' }}
          >
            Fecha vencimiento
          </TableCell>
          <TableCell
            className={classes.tableCell}
            style={{ textAlign: 'center' }}
          >
            Días
          </TableCell>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {selectedDocuments.flatMap((row: any, rowIndex: number) => {
            let subDocumentRows = [];
            const isExpanded = !!expandedRows[rowIndex];
            const hasSubDocuments =
              row.subDocuments && row.subDocuments.length > 0;
            const parentRow = (
              <TableRow key={rowIndex}>
                <TableCell style={firstCellStyle}>
                  <IconButton onClick={() => addSubDocument(rowIndex)}>
                    <Add />
                  </IconButton>
                  {row.subDocuments &&
                    row.subDocuments.length > 0 &&
                    row.subDocuments.length}{' '}
                  <IconButton
                    onClick={() => toggleRowExpansion(rowIndex)}
                    aria-label="expand row"
                    size="small"
                  >
                    {row.subDocuments && row.subDocuments.length > 0 ? (
                      expandedRows[rowIndex] ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )
                    ) : null}
                  </IconButton>
                </TableCell>

                <TableCell style={firstCellStyle}>{row.folio}</TableCell>
                {row.code && (
                  <TableCell style={firstCellStyle}>{row.code}</TableCell>
                )}
                {isExpress && (
                  <TableCell style={firstCellStyle}>
                    <IconButton
                      onClick={(e) => toggleRowExpansionPdf(rowIndex, e)}
                      color={row.fileStatus ? 'primary' : 'inherit'}
                    >
                      <Receipt />
                      <Menu
                        anchorEl={anchorEls[rowIndex]}
                        open={openMenus[rowIndex]}
                      >
                        <MenuItem
                          onClick={() => handleDownloadDocumentPDF(row.id)}
                          disabled={!row.fileStatus}
                        >
                          Descargar
                        </MenuItem>
                        <MenuItem onClick={() => handleUploadDocumentPDF()}>
                          Subir
                        </MenuItem>
                      </Menu>
                    </IconButton>
                  </TableCell>
                )}
                <TableCell
                  className={classes.tableCell}
                  style={{ textAlign: 'right' }}
                >
                  {row.stakeholderName}
                </TableCell>
                <TableCell
                  className={classes.tableCell}
                  style={{ textAlign: 'right' }}
                >
                  <TextField
                    value={formatMoney(row.amountAvailable)}
                    inputProps={{ style: { textAlign: 'right' } }}
                    onChange={(e) =>
                      handleChangeDocumentAmount(e.target.value, rowIndex)
                    }
                    disabled={
                      (row.subDocuments && row.subDocuments.length > 0) ||
                      !hasAnyPermissions(user, [
                        'financing_factoring_edit_documents_amount',
                      ])
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LightTooltip
                            title={
                              <Typography style={{ padding: '8px' }}>
                                Disminuir monto por nota de crédito.
                              </Typography>
                            }
                            placement="top"
                          >
                            <span>
                              <IconButton
                                color="warning"
                                style={{ cursor: 'pointer' }}
                                disabled={
                                  row.creditNoteAmount == null ||
                                  row.creditNoteAmount <= 0 ||
                                  row.creditNoteAmount > row.amount ||
                                  row._creditNoteApplied ||
                                  !hasAnyPermissions(user, [
                                    'financing_factoring_edit_documents_amount',
                                  ])
                                }
                                onClick={() => {
                                  if (
                                    row.creditNoteAmount &&
                                    row.creditNoteAmount > 0 &&
                                    row.creditNoteAmount <= row.amount &&
                                    !row._creditNoteApplied
                                  ) {
                                    const newSelectedDocuments =
                                      selectedDocuments.map(
                                        (doc: any, i: any) => {
                                          if (i === rowIndex) {
                                            return {
                                              ...doc,
                                              amount:
                                                doc.amount -
                                                doc.creditNoteAmount,
                                              amountAvailable:
                                                doc.amount -
                                                doc.creditNoteAmount,
                                              _creditNoteApplied: true,
                                            };
                                          }
                                          return doc;
                                        },
                                      );
                                    setSelectedDocuments(newSelectedDocuments);
                                  }
                                }}
                              >
                                <SettingsBackupRestore />
                              </IconButton>
                            </span>
                          </LightTooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </TableCell>
                {anyHasSubDocuments && (
                  <TableCell
                    className={classes.tableCell}
                    style={{ textAlign: 'right' }}
                  >
                    {formatMoney(row.amountAvailable)}
                  </TableCell>
                )}
                <TableCell
                  className={classes.tableCell}
                  style={{ textAlign: 'right' }}
                >
                  {formatDate(row.issuedDate)}
                </TableCell>
                <TableCell
                  className={classes.tableCell}
                  style={{ textAlign: 'right' }}
                >
                  {hasSubDocuments ? null : (
                    <TextField
                      value={row.tempDueDate}
                      type="date"
                      onChange={(e) =>
                        handleChangeTempDate(e.target.value, rowIndex)
                      }
                      onBlur={() => handleChangeDateNormal(rowIndex)}
                    />
                  )}
                </TableCell>
                <TableCell
                  className={classes.tableCell}
                  style={{ textAlign: 'center' }}
                >
                  {row.daysToCount}
                </TableCell>
              </TableRow>
            );

            if (isExpanded && hasSubDocuments) {
              subDocumentRows = row.subDocuments.map(
                (subDoc: any, subIndex: number) => (
                  <TableRow key={`sub-${rowIndex}-${subIndex}`}>
                    <TableCell>
                      <IconButton
                        onClick={() => deleteSubDocument(rowIndex, subIndex)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                    <TableCell
                      className={classes.tableCell}
                      style={{ textAlign: 'left' }}
                    >
                      {subDoc.isOld ? (
                        subDoc.folio
                      ) : (
                        <TextField
                          value={subDoc.folio}
                          label="Folio"
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(
                              /[^0-9]/g,
                              '',
                            );
                            handleChangeSubDocument(
                              numericValue,
                              'folio',
                              rowIndex,
                              subIndex,
                            );
                          }}
                        />
                      )}
                    </TableCell>
                    {row.code && (
                      <TableCell
                        className={classes.tableCell}
                        style={{ textAlign: 'right' }}
                      ></TableCell>
                    )}
                    {isExpress && (
                      <TableCell style={firstCellStyle}>-</TableCell>
                    )}
                    <TableCell
                      className={classes.tableCell}
                      style={{ textAlign: 'right' }}
                    >
                      {subDoc.stakeholderName}{' '}
                    </TableCell>
                    <TableCell
                      className={classes.tableCell}
                      style={{ textAlign: 'right' }}
                    >
                      <TextField
                        value={formatMoney(subDoc.amount)}
                        inputProps={{ style: { textAlign: 'right' } }}
                        label="Monto"
                        onChange={(e) =>
                          handleChangeSubDocument(
                            e.target.value,
                            'amount',
                            rowIndex,
                            subIndex,
                          )
                        }
                      />
                    </TableCell>
                    <TableCell
                      className={classes.tableCell}
                      style={{ textAlign: 'right' }}
                    ></TableCell>
                    <TableCell
                      className={classes.tableCell}
                      style={{ textAlign: 'right' }}
                    >
                      {formatDate(subDoc.issuedDate)}
                    </TableCell>
                    <TableCell
                      className={classes.tableCell}
                      style={{ textAlign: 'right' }}
                    >
                      <TextField
                        value={subDoc.tempDueDate}
                        type="date"
                        onChange={(e) =>
                          handleChangeSubDocumentTempDate(
                            e.target.value,
                            rowIndex,
                            subIndex,
                          )
                        }
                        onBlur={() =>
                          handleChangeSubDocumentDateNormal(rowIndex, subIndex)
                        }
                      />
                    </TableCell>
                    <TableCell
                      className={classes.tableCell}
                      style={{ textAlign: 'center' }}
                    >
                      {subDoc.daysToCount}
                    </TableCell>
                  </TableRow>
                ),
              );
            }
            // Combine the parent row with its sub-document rows
            return [parentRow, ...subDocumentRows];
          })}
        </TableBody>
      </Table>
      {openUploadDocumentPDF && (
        <Dialog
          open={openUploadDocumentPDF}
          onClose={() => setOpenUploadDocumentPDF(false)}
        >
          <DialogTitle id="form-dialog-title">Subir factura</DialogTitle>
          {loading ? (
            <CenteredCircularProgress type="dialog" />
          ) : (
            <DialogContent style={{ minHeight: 100, padding: 50 }}>
              <Uploader
                buttonText="Subir documento"
                handleFileChange={handleFileChange}
                uploadedFileName={uploadedFileName}
                setUploadedFileName={setUploadedFileName}
              />
            </DialogContent>
          )}
          <DialogActions>
            <CustomButton
              onClick={() => setOpenUploadDocumentPDF(false)}
              color="secondary"
            >
              Cancelar
            </CustomButton>
            <CustomButton onClick={uploadFiles} color="primary">
              Subir
            </CustomButton>
          </DialogActions>
        </Dialog>
      )}
    </TableContainer>
  );
};

export default DocumentsTable;
